import heroStyles from './TextMediaModule.hero.module.scss';
import defaultStyles from './TextMediaModule.module.scss';

import cx from 'classnames';
import type { ReferencedEntry } from 'contentful';
import React, { AllHTMLAttributes, FC } from 'react';

import {
  ContentfulMargin,
  ContentfulPadding,
  HasImage,
  HasVideo,
  MediaAssembly,
  TextAlignment,
  TextMediaHeroType,
  TextMediaModuleType,
  Weight,
} from 'api/contentful/contentTypes.cloverdotcom';
import { FadeToastUp } from 'components/Animation/FadeToastUp/FadeToastUp';
import { DisplayMode, EnhancedMedia } from 'components/EnhancedMedia/EnhancedMedia';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useContentEntriesContext } from 'components/ContentEntriesProvider/ContentEntriesContext';
import { TextStack } from 'components/TextStack/TextStack';
import { getField } from 'utils/EntryUtil';
import { getBackgroundImageUrl } from './getBackgroundImageUrl';
import { useOverrideTopic } from './useOverrideTopic';
import { VideoPlayer } from '@clover-growth/video-player';

/**
 * Props that are shared between the Presentational and Container components
 */
type SharedProps = {
  mediaClassName?: string;
  textBlockClassName?: string;
  blurbClassName?: string;
  containerClassName?: string;
  titleClassName?: string;
  titleTagName?: keyof React.ReactHTML;
  mediaRef?: React.Ref<EnhancedMedia>;
  threshold?: number;
  disableAnimation?: boolean;
};

interface ITextMediaModulePresentationalProps
  extends SharedProps,
    MediaAssembly,
    AllHTMLAttributes<HTMLElement> {
  mediaOverride?: ReferencedEntry<HasVideo | HasImage>;
  wrapperRef?: React.Ref<HTMLDivElement>;
}

export const TextMediaModulePresentational: FC<ITextMediaModulePresentationalProps> = ({
  topic,
  mediaOverride,
  mediaRef,
  textAlignment = TextAlignment.LEFT,
  className,
  moduleType,
  heroType,
  textBlockClassName,
  mediaClassName,
  containerClassName,
  titleClassName,
  blurbClassName,
  wrapperRef,
  threshold,
  disableAnimation,
  fontWeight,
  topPadding = ContentfulPadding.MEDIUM,
  bottomPadding,
  bottomMargin,
  backgroundImage,
  backgroundColor,
  titleTagName = 'h2',
  ...props
}) => {
  const media = mediaOverride ?? topic;

  const image = getField(media, 'image');
  const video = getField(media as ReferencedEntry<HasVideo>, 'video');
  const hasImageOnly = image && !video;

  const isCentered = textAlignment === TextAlignment.CENTER;
  const isYoutubeModal = !!(getField(topic, 'youtubeId') && getField(topic, 'image'));
  const isTextMediaModule = true; // Used in EnhancedMedia to prevent rendering images as backgrounds
  const hasMedia = Boolean(getField(topic, 'media') && getField(topic, 'media')?.fields);
  const hasYoutubeVideo = !!getField(topic, 'youtubeId');
  const hasText = Boolean(getField(topic, 'blurb') || getField(topic, 'title'));
  const styles = moduleType === TextMediaModuleType.HERO ? heroStyles : defaultStyles;
  const overrideTopic = useOverrideTopic(topic, moduleType, heroType, !!(backgroundImage ?? backgroundColor));
  const isTransparent = useContentEntriesContext().activePage?.transparentNavBar;
  const videoPlayerProps = getField(topic, 'videoPlayer')?.fields;
  const isValidVideoPlayer = videoPlayerProps?.videoUrl || videoPlayerProps?.media?.fields.file.url;

  return (
    <section
      className={cx(styles.moduleWrapper, className, {
        [styles.moduleWrapperNoLeftRightPadding]:
          (isCentered && !video) || textAlignment === TextAlignment.TOP,
        [styles.headline]: moduleType === TextMediaModuleType.HEADLINE,
        [styles.hero]: moduleType === TextMediaModuleType.HERO,
        [styles.collage]: heroType === TextMediaHeroType.COLLAGE,
        [styles.fullbleed]: heroType === TextMediaHeroType.FULLBLEED,
        [styles.hardware]: heroType === TextMediaHeroType.HARDWARE,
        [styles.cta]: heroType === TextMediaHeroType.CTA,
        [styles.moduleWrapperMarginBottomNone]: bottomMargin === ContentfulMargin.NONE,
        [styles.moduleWrapperPaddingTopNone]: topPadding === ContentfulPadding.NONE,
        [styles.moduleWrapperPaddingTopSmall]: topPadding === ContentfulPadding.SMALL,
        [styles.moduleWrapperPaddingTopMedium]: topPadding === ContentfulPadding.MEDIUM,
        [styles.moduleWrapperPaddingTopLarge]: topPadding === ContentfulPadding.LARGE,
        [styles.moduleWrapperPaddingBottomNone]: bottomPadding === ContentfulPadding.NONE,
        [styles.moduleWrapperPaddingBottomSmall]: bottomPadding === ContentfulPadding.SMALL,
        [styles.moduleWrapperPaddingBottomMedium]: bottomPadding === ContentfulPadding.MEDIUM,
        [styles.moduleWrapperPaddingBottomLarge]: bottomPadding === ContentfulPadding.LARGE,
        [styles.isTransparent]: isTransparent,
      })}
      {...props}
    >
      <div
        ref={wrapperRef}
        className={cx(styles.container, containerClassName, {
          [cx(styles.containerCenter, styles.containerCenterColumn)]: isCentered,
          [styles.containerCenterBackgroundImg]: isCentered && hasImageOnly,
          [styles.containerTextLeft]: !isCentered && textAlignment === TextAlignment.LEFT,
          [styles.containerTextRight]: !isCentered && textAlignment === TextAlignment.RIGHT,
          [styles.containerTextTop]: !isCentered && textAlignment === TextAlignment.TOP,
        })}
      >
        <FadeToastUp disableAnimation={disableAnimation} threshold={threshold}>
          {({ className: fadeToastUpClassName, style, ref }) =>
            isValidVideoPlayer ? (
              <div className={styles.videoWrapper}>
                <VideoPlayer
                  {...videoPlayerProps}
                  className={styles.videoPlayer}
                  videoUrl={videoPlayerProps.media?.fields.file.url ?? videoPlayerProps.videoUrl ?? ''}
                  posterUrl={videoPlayerProps.thumbnail?.fields.file.url}
                  inlineControls="over-player"
                  theme="white"
                />
              </div>
            ) : (
              <EnhancedMedia
                innerRef={ref as React.Ref<HTMLDivElement>}
                ref={mediaRef}
                entry={media}
                displayMode={isCentered ? DisplayMode.MEDIA_ONLY : DisplayMode.AUTO}
                className={cx(fadeToastUpClassName, mediaClassName, {
                  [styles.imageWrapper]: hasImageOnly,
                  [styles.imageWrapperCenter]: hasImageOnly && isCentered,
                  [styles.videoWrapper]: video || hasMedia || hasYoutubeVideo,
                  [styles.videoWrapperCenter]: (video || hasMedia || hasYoutubeVideo) && isCentered,
                })}
                style={style}
                loop={video && !isCentered}
                autoPlay={false}
                muted={video && !isCentered}
                controls={video && isCentered}
                useBackgroundCover={textAlignment === TextAlignment.TOP}
                isYoutubeModal={isYoutubeModal}
                isTextMediaModule={isTextMediaModule}
              />
            )
          }
        </FadeToastUp>
        <FadeToastUp disableAnimation={disableAnimation} delayMultiplier={1.5}>
          {({ className: fadeToastUpClassName, style, ref }) => (
            <TextStack
              innerRef={ref as React.Ref<HTMLDivElement>}
              entry={overrideTopic || topic}
              className={cx(fadeToastUpClassName, styles.textWrapper, textBlockClassName, {
                [styles.textWrapperCenter]: isCentered,
                [styles.textWrapperLeft]: textAlignment === TextAlignment.LEFT,
                [styles.textWrapperRight]: textAlignment === TextAlignment.RIGHT,
                [styles.textWrapperTop]: textAlignment === TextAlignment.TOP,
                [styles.textWrapperLightWeight]: fontWeight === Weight.LIGHT,
                [styles.textWrapperRegularWeight]: fontWeight === Weight.REGULAR,
                [styles.textWrapperBoldWeight]: fontWeight === Weight.BOLD,
                [styles.textWrapperNoText]: !hasText,
              })}
              style={style}
              linkClassName={cx(styles.ctaButton, { [styles.ctaButtonCenter]: isCentered })}
              linkContainerClassName={styles.ctaContainer}
              blurbClassName={cx(blurbClassName, styles.blurb)}
              titleClassName={cx(titleClassName, styles.title, {
                [styles.titleTopAligned]: textAlignment === TextAlignment.TOP,
                [styles.titleWithColor]: !props?.style?.color,
              })}
              subTitleClassName={styles.subTitle}
              titleTagName={titleTagName}
              isButton
            />
          )}
        </FadeToastUp>
      </div>
    </section>
  );
};

interface ITextMediaModuleProps extends SharedProps, AllHTMLAttributes<HTMLElement> {
  entry?: ReferencedEntry<MediaAssembly>;
}

export const TextMediaModule: FC<ITextMediaModuleProps> = ({ entry, style, ...props }) => {
  const { browser } = useBreakpoints();
  if (!entry?.fields) return null;

  const { headingTagName, backgroundColor, backgroundImage, internalName, ...entryProps } = entry.fields;

  return (
    <TextMediaModulePresentational
      style={{
        backgroundColor,
        backgroundImage: getBackgroundImageUrl(browser, backgroundImage),
        ...style,
      }}
      backgroundImage={backgroundImage}
      backgroundColor={backgroundColor}
      titleTagName={headingTagName}
      {...entryProps}
      {...props}
    />
  );
};

export default TextMediaModule;
